import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button, Card, Nav, Tab, Spinner, Alert } from "react-bootstrap";
import { getUserById } from "../../services/UserService";
import {
  FaArrowLeft,
  FaEdit,
  FaUserCircle,
  FaBoxes,
  FaHistory,
} from "react-icons/fa";
import { VscSymbolMethod } from "react-icons/vsc";
import { roleStyleMap, roleDisplayMap } from "../../utilities";
import EditUser from "./EditUser";
import UserOverviewTab from "./UserOverviewTab";
import UserAssetsTab from "./UserAssetsTab";
import UserLogTab from "./UserLogTab";
import DepartmentAsset from "./DepartmentTab";

const UserDetailsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getUserById(userId);
        setUser(res.data);
      } catch (err) {
        console.error("Failed to fetch user details:", err);
        setError("Failed to load user details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const handleUpdateSuccess = (updatedUser) => {
    setUser((prev) => ({
      ...prev,
      ...updatedUser,
      fullname: updatedUser.fullname || prev.fullname,
    }));
  };


  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <Alert variant="danger">
          {error}
          <Button
            variant="outline-primary"
            onClick={() => navigate(-1)}
            className="mt-1"
          >
            <FaArrowLeft className="me-2" />
            Go Back
          </Button>
        </Alert>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-5">
        <Alert variant="warning">User not found</Alert>
        <Button variant="outline-primary" onClick={() => navigate(-1)}>
          <FaArrowLeft className="me-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-2">
      <EditUser
        showEditOffcanvas={showEditModal}
        setShowEditOffcanvas={setShowEditModal}
        userData={user}
        onUserUpdated={handleUpdateSuccess}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="text-primary">User Details</h4>
          <nav>
            <ol className="breadcrumb breadcrumb-arrow mb-0">
              <li className="breadcrumb-item">
                <Link to="/">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/users">User List</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {user.fullname}
              </li>
            </ol>
          </nav>
        </div>

        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setShowEditModal(true)}
        >
          <FaEdit className="me-2" />
          Edit User
        </Button>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Card className="p-4 mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div className="d-flex align-items-center">
              <div
                className="user-avatar rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-3"
                style={{ width: 48, height: 48, fontSize: 20 }}
              >
                {(user.first_name?.[0]?.toUpperCase() || "") +
                  (user.last_name?.[0]?.toUpperCase() || "") || "U"}
              </div>
              <div>
                <h6 className="mb-1">{user.fullname}</h6>
                <div>{user.email}</div>
                <span
                  className={`badge ${
                    roleStyleMap[user.role] || "bg-secondary"
                  } mt-1`}
                >
                  {roleDisplayMap[user.role] || user.role}
                </span>
              </div>
            </div>

            <Nav className="gap-2 mt-3 mt-md-0" variant="pills">
              <Nav.Item>
                <Nav.Link eventKey="overview">
                  <FaUserCircle size={14} /> Overview
                </Nav.Link>
              </Nav.Item>

              {user.role !== "AUDITOR" && (
                <>
                  <Nav.Item>
                    <Nav.Link eventKey="assets">
                      <FaBoxes size={14} /> My Assets
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="log">
                      <FaHistory size={14} /> Log
                    </Nav.Link>
                  </Nav.Item>
                </>
              )}

              {user.role === "HOD" && (
                <Nav.Item>
                  <Nav.Link eventKey="department">
                    <VscSymbolMethod size={18} /> Department
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </div>
        </Card>

        <Tab.Content className="mt-3">
          <Tab.Pane eventKey="overview">
            <UserOverviewTab user={user} />
          </Tab.Pane>
          <Tab.Pane eventKey="assets">
            <UserAssetsTab userId={userId} />
          </Tab.Pane>
          {user.role === "HOD" && (
            <Tab.Pane eventKey="department">
              <DepartmentAsset user={user} />
            </Tab.Pane>
          )}
          <Tab.Pane eventKey="log">
            <UserLogTab userId={userId} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default UserDetailsPage;
